import React, { useState } from 'react';
import { AlertTriangle, ChevronRight, WifiOff, Camera, MapPin, Copy, Check, Share2 } from 'lucide-react'; // Importa ícones
import { copyToClipboard } from '../../../utils/clipboard';

/**
 * @description Card para exibir um resumo de uma troca de poste.
 *
 * @param {object} props - As propriedades do componente.
 * @param {object} props.trade - O objeto contendo os dados da troca.
 * @param {function} props.onCardClick - Função a ser chamada quando o card for clicado.
 * @param {function} props.onShareClick - Função a ser chamada quando o botão de compartilhar for clicado.
 */
const TradeCard = ({ trade, onCardClick, onShareClick }) => {
    // Desestruturação dos dados da troca com valores padrão
    const {
        serviceNote = 'N/A',
        address = 'Endereço não informado',
        neighborhood = 'Bairro não informado',
        city = 'Cidade não informada',
        ftthSector = 'N/D',
        isCritical = false,
        isNoNetwork = false,
        isCanceled = false,
        photos = [],
        coordinates, // Coordenadas para o mapa
        poles = 0,
        servicePeriodStart,
        startTime = '',
        endTime = ''
    } = trade;

    const [copySuccess, setCopySuccess] = useState(false);

    const handleCopyServiceNote = async (e) => {
        e.stopPropagation();
        const success = await copyToClipboard(serviceNote);
        if (success) {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }
    };

    const handleKeyDown = (e) => {
        // Ignora se o evento se originou de um elemento interativo interno
        if (e.target.tagName === 'BUTTON' || e.target.closest('button') || e.target.tagName === 'A' || e.target.closest('a')) {
            return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onCardClick(trade);
        }
    };

    // Verificação se a data da troca é hoje
    const isToday = (() => {
        if (!servicePeriodStart) return false;
        const [day, month, year] = servicePeriodStart.split('/');
        const today = new Date();
        return (
            today.getDate() === parseInt(day, 10) &&
            today.getMonth() + 1 === parseInt(month, 10) &&
            today.getFullYear() === parseInt(year, 10)
        );
    })();

    // Formata a data para DD/MM
    const formattedDate = servicePeriodStart ? servicePeriodStart.substring(0, 5) : '';

    return (
        // Container principal do card, agora sempre clicável
        <div
            className={`bg-white dark:bg-slate-800/50 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 p-4 flex items-center justify-between border border-transparent cursor-pointer hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800 ${isCanceled ? 'opacity-60 bg-slate-100 dark:bg-slate-800/20' : ''}`}
            onClick={() => onCardClick(trade)} // Permite o clique mesmo se cancelado
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={0}
            aria-label={`Ver detalhes da troca ${serviceNote}`}
        >
            <div className="flex-grow">
                {/* LINHA SUPERIOR: NS, Badges e Ícones de Status */}
                <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center flex-wrap gap-2">
                        <div className="flex items-center">
                            <span className="text-sm font-semibold text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/50 py-1 px-2 rounded">
                                NS: {serviceNote}
                            </span>
                            <button 
                                onClick={handleCopyServiceNote} 
                                title={copySuccess ? "NS copiada!" : "Copiar NS"}
                                aria-label={copySuccess ? "NS copiada!" : "Copiar NS"}
                                className="p-1 rounded-full text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-slate-800"
                            >
                                {copySuccess ? (
                                    <Check className="h-4 w-4 text-green-500" />
                                ) : (
                                    <Copy className="h-4 w-4" />
                                )}
                            </button>
                        </div>
                        {isToday && !isCanceled && (
                            <span className="text-xs font-bold text-orange-500 bg-orange-100 dark:bg-orange-900/40 py-1 px-2 rounded animate-pulse uppercase">
                                Hoje
                            </span>
                        )}
                        {isCanceled && (
                            <span className="text-xs font-bold text-red-600 bg-red-100 dark:bg-red-900/40 py-1 px-2 rounded uppercase">
                                Cancelada
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                        {/* --- CORREÇÃO APLICADA AQUI --- */}
                        {/* O ícone da câmera é exibido se houver fotos e a troca não estiver cancelada */}
                        {photos && photos.length > 0 && !isCanceled && (
                            <div className="flex items-center gap-1 text-fuchsia-500 bg-fuchsia-100 dark:bg-fuchsia-900/40 px-2 py-1 rounded-md" title="Possui fotos anexadas" aria-label="Possui fotos anexadas" role="status">
                                <Camera size={16} />
                                <span className="text-xs font-bold uppercase hidden sm:inline">Fotos</span>
                            </div>
                        )}
                        {isNoNetwork && !isCanceled && (
                            <div className="flex items-center gap-1 text-red-500 bg-red-100 dark:bg-red-900/40 px-2 py-1 rounded-md" title="Sem Rede" aria-label="Sem Rede" role="status">
                                <WifiOff size={16} />
                                <span className="text-xs font-bold uppercase hidden sm:inline">Sem Rede</span>
                            </div>
                        )}
                        {isCritical && !isCanceled && (
                            <div className="flex items-center gap-1 text-orange-500 bg-orange-100 dark:bg-orange-900/40 px-2 py-1 rounded-md" title="Intervenção Crítica" aria-label="Intervenção Crítica" role="status">
                                <AlertTriangle size={16} />
                                <span className="text-xs font-bold uppercase hidden sm:inline">Crítica</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Endereço */}
                <h3 className="capitalize text-md font-bold text-slate-800 dark:text-slate-100 mb-1">
                    {address.toLowerCase()} {poles > 0 && `(${poles} poste${poles > 1 ? 's' : ''})`}
                </h3>
                <p className="capitalize text-sm text-slate-500 dark:text-slate-400 mb-3">
                    {neighborhood.toLowerCase()}, {city.toLowerCase()}
                </p>

                {/* LINHA INFERIOR: Área Afetada e Data/Hora */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-3">
                    <div className="text-sm flex items-center gap-2">
                        {coordinates && coordinates.lat && coordinates.lng && !isCanceled && onShareClick && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onShareClick(trade); }}
                                className="inline-flex items-center text-slate-600 dark:text-slate-300 bg-slate-200 dark:bg-slate-700 px-2 py-1 rounded-md hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-slate-800"
                                title="Compartilhar localização da troca"
                                aria-label="Compartilhar localização da troca"
                            >
                                <Share2 size={16} className="text-green-600 dark:text-green-400 mr-1.5" />
                                <span className="uppercase font-mono font-bold hidden sm:inline">Compartilhar</span>
                            </button>
                        )}
                        {coordinates && coordinates.lat && coordinates.lng && !isCanceled ? (
                            <a
                                href={`https://www.google.com/maps/search/?api=1&query=${coordinates.lat},${coordinates.lng}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center text-slate-600 dark:text-slate-300 bg-slate-200 dark:bg-slate-700 px-2 py-1 rounded-md hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-slate-800"
                                title="Ver no mapa"
                                aria-label={`Ver localização da troca no mapa: setor ${ftthSector}`}
                            >
                                <MapPin size={16} className="text-blue-500 dark:text-blue-400 mr-1.5" />
                                <span className="uppercase font-mono" title={ftthSector}>
                                    <span className="md:hidden">
                                        {ftthSector.length > 17 ? `${ftthSector.substring(0, 17)}...` : ftthSector}
                                    </span>
                                    <span className="hidden md:inline">
                                        {ftthSector.length > 25 ? `${ftthSector.substring(0, 25)}...` : ftthSector}
                                    </span>
                                </span>
                            </a>
                        ) : (
                            <span className="uppercase font-mono text-slate-600 dark:text-slate-300 bg-slate-200 dark:bg-slate-700 px-2 py-1 rounded-md" title={ftthSector}>
                                <span className="md:hidden">
                                    {ftthSector.length > 17 ? `${ftthSector.substring(0, 17)}...` : ftthSector}
                                </span>
                                <span className="hidden md:inline">
                                    {ftthSector.length > 25 ? `${ftthSector.substring(0, 25)}...` : ftthSector}
                                </span>
                            </span>
                        )}
                    </div>
                    {(startTime || endTime) && (
                         <div className="text-sm text-slate-500 dark:text-slate-400 font-mono text-left sm:text-right">
                            <span>{formattedDate}</span>
                            <span className="ml-2">{startTime} - {endTime}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Seta de Detalhes */}
            <div className="ml-4 flex-shrink-0 self-center">
                <div className={`p-2 rounded-full ${isCanceled ? 'text-slate-400' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors'}`}>
                    <ChevronRight size={24} />
                </div>
            </div>
        </div>
    );
};

// Optimized: Wrapped in React.memo with custom comparison to prevent unnecessary re-renders
// when parent component updates and creates new trade object references without actual data changes.
const tradeAreEqual = (prevProps, nextProps) => {
    if (prevProps.onCardClick !== nextProps.onCardClick) return false;
    if (prevProps.onShareClick !== nextProps.onShareClick) return false;

    const t1 = prevProps.trade;
    const t2 = nextProps.trade;

    if (!t1 || !t2) return t1 === t2;
    if (t1.id !== t2.id) return false;
    if (t1.updatedAt !== t2.updatedAt) return false;

    // Fallback comparison for rendered fields
    if (t1.serviceNote !== t2.serviceNote) return false;
    if (t1.address !== t2.address) return false;
    if (t1.neighborhood !== t2.neighborhood) return false;
    if (t1.city !== t2.city) return false;
    if (t1.ftthSector !== t2.ftthSector) return false;
    if (t1.servicePeriodStart !== t2.servicePeriodStart) return false;
    if (t1.servicePeriodEnd !== t2.servicePeriodEnd) return false;
    if (t1.isCanceled !== t2.isCanceled) return false;
    if (t1.isNoNetwork !== t2.isNoNetwork) return false;

    return true;
};

export default React.memo(TradeCard, tradeAreEqual);
